import { ACCENT, ACCENT2, BG2, BORDER, CARD, DARK, DIM, MID, RED } from "../theme";
import { EXCEL_LOAD_ERROR, loadXLSX } from "../lib/excel";
import { supabase } from "../lib/supabase";
import { applyUpload, deleteResultMessage, deleteUploadBatch, loadExistingUploads, uploadIds } from "../lib/uploadBatch";
import { parseUploadRows, planUpload, summaryLine, undatedNotice } from "../uploadPlan";
import { useEffect, useRef, useState } from "react";

async function parseUploadFile(file) {
  let XLSX;
  try {
    XLSX = await loadXLSX();
  } catch (err) {
    return { jobs: [], revenue: [], expenses: [], errors: [EXCEL_LOAD_ERROR] };
  }
  try {
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: 'array', cellDates: true });
    function sheetRows(name) {
      const ws = wb.Sheets[name];
      if (!ws) return [];
      return XLSX.utils.sheet_to_json(ws, { defval: '' });
    }
    return parseUploadRows({ jobRows: sheetRows('Jobs'), revRows: sheetRows('Revenue'), expRows: sheetRows('Expenses') });
  } catch (err) {
    return { jobs: [], revenue: [], expenses: [], errors: [`Parse error: ${err.message}`] };
  }
}

function UploadData({ userId, onDataRefresh }) {
  const [step, setStep] = useState('idle'); // idle | preview | importing | success
  const [parsedData, setParsedData] = useState(null);
  const [fileName, setFileName] = useState('');
  const [batches, setBatches] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [deletingBatch, setDeletingBatch] = useState(null);
  const [importError, setImportError] = useState('');
  const [plan, setPlan] = useState(null);
  const [deleteMessage, setDeleteMessage] = useState('');
  const [deleteFailed, setDeleteFailed] = useState(false);
  const [templateError, setTemplateError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [parsing, setParsing] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => { if (userId) loadBatches(); }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadBatches() {
    setLoadingBatches(true);
    const { data } = await supabase.from('upload_batches')
      .select('*').eq('contractor_id', userId).order('uploaded_at', { ascending: false });
    setBatches(data || []);
    setLoadingBatches(false);
  }

  async function handleFile(file) {
    if (!file || !file.name.endsWith('.xlsx')) { alert('Please select an .xlsx file.'); return; }
    setFileName(file.name);
    setParsing(true);
    setImportError('');
    setDeleteMessage('');
    const result = await parseUploadFile(file);
    // Compare with what this business already uploaded, so the preview can say what will really happen.
    let nextPlan = null;
    let planError = '';
    if (result.jobs.length > 0) {
      const existing = await loadExistingUploads(supabase, userId);
      if (existing.error) planError = `Could not check your earlier uploads (${existing.error.message || 'unknown error'}), so this file cannot be imported safely yet. Please try again.`;
      else nextPlan = planUpload(result, existing.jobs, existing.txns, uploadIds(userId), { withCategory: existing.withCategory });
    }
    setParsedData(result);
    setPlan(nextPlan);
    setImportError(planError);
    setParsing(false);
    setStep('preview');
  }

  async function handleImport() {
    if (!parsedData || !plan || !userId) return;
    setStep('importing');
    setImportError('');
    try {
      await applyUpload(supabase, userId, fileName, plan);
      setStep('success');
      loadBatches();
      if (onDataRefresh) onDataRefresh();
    } catch (err) {
      setImportError(`${err.message}. Some rows may already be saved. Choose the same file again and import it again: rows that were saved will be recognized, not doubled.`);
      setStep('preview');
      loadBatches();
      if (onDataRefresh) onDataRefresh();
    }
  }

  async function handleDeleteBatch(batchId) {
    if (!window.confirm('Delete this upload? The jobs, revenue and expenses it added will be removed. QuickBooks data and anything you entered yourself stays.')) return;
    setDeletingBatch(batchId);
    setDeleteMessage('');
    let result;
    try {
      result = await deleteUploadBatch(supabase, userId, batchId);
    } catch (err) {
      result = { ok: false, removed: { jobs: 0, transactions: 0 }, keptJobs: 0, problems: [err.message || 'Something went wrong.'] };
    }
    setDeleteMessage(deleteResultMessage(result));
    setDeleteFailed(!result.ok);
    setDeletingBatch(null);
    loadBatches();
    if (onDataRefresh) onDataRefresh();
  }

  async function downloadTemplate() {
    let XLSX;
    try {
      XLSX = await loadXLSX();
    } catch (err) {
      setTemplateError(EXCEL_LOAD_ERROR);
      return;
    }
    setTemplateError('');
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
      ['Canopy Upload Template v1'], [''],
      ['1. Fill in the Jobs tab first. Revenue and Expenses must reference a job name from that tab.'],
      ['2. Columns marked * are required.'],
      ['3. Amount: numbers only — no $ or commas (e.g. 4200, not $4,200)'],
      ['4. Date format: YYYY-MM-DD (e.g. 2024-03-15)'],
      ['5. Job names are matched case-insensitively across tabs.'],
      ['6. Delete example rows before uploading.'],
    ]), 'Instructions');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
      ['Job Name*', 'Client Name', 'Job Type', 'Status', 'Start Date', 'End Date'],
      ['Example: Website redesign', 'Johnson Family', 'Design', 'Complete', '2024-01-05', '2024-02-15'],
    ]), 'Jobs');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
      ['Job Name*', 'Description', 'Amount*', 'Date*', 'Invoice/Doc#'],
      ['Example: Website redesign', 'Final payment', '48500', '2024-02-15', 'INV-001'],
    ]), 'Revenue');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
      ['Job Name*', 'Vendor', 'Description*', 'Amount*', 'Date*', 'Category*'],
      ['Example: Website redesign', 'Acme Supply', 'Printing and materials', '4200', '2024-01-10', 'Materials'],
    ]), 'Expenses');
    XLSX.writeFile(wb, 'canopy-upload-template.xlsx');
  }

  const hasErrors = (parsedData?.errors?.length || 0) > 0;
  const validJobCount = parsedData?.jobs?.length || 0;
  const validRevCount = parsedData?.revenue?.filter(r => r.matched).length || 0;
  const validExpCount = parsedData?.expenses?.filter(e => e.matched).length || 0;
  const nothingToDo = !!plan && plan.summary.added === 0 && plan.summary.updated === 0;

  return (
    <div className="r-page" style={{ padding: "32px 36px", maxWidth: 800 }}>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontFamily: "'Lora',serif", fontSize: 22, fontWeight: 600, color: DARK, marginBottom: 6 }}>Upload Data</h1>
        <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: MID, lineHeight: 1.6, maxWidth: 540, marginBottom: 0 }}>
          Import jobs, revenue, and expenses from outside QuickBooks using the Canopy template. Uploaded data appears alongside your QuickBooks data throughout the app.
        </p>
        <button className="btn" onClick={downloadTemplate} style={{ marginTop: 14, display: "inline-flex", alignItems: "center", gap: 7 }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          Download Template
        </button>
        {templateError && <div role="alert" style={{ marginTop: 10, fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: RED }}>{templateError}</div>}
      </div>

      {/* Upload zone */}
      <div className="r-card-pad" style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 8, padding: 28, marginBottom: 24 }}>

        {/* Idle — file picker */}
        {step === 'idle' && (
          <>
            {parsing ? (
              <div style={{ textAlign: "center", padding: "28px 0" }}>
                <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: DIM }}>Parsing {fileName}…</div>
              </div>
            ) : (
              <>
                <div
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={e => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) handleFile(f); }}
                  style={{ border: `2px dashed ${dragOver ? ACCENT2 : BORDER}`, borderRadius: 8, padding: "36px 24px", textAlign: "center", cursor: "pointer", transition: "border-color 0.15s", background: dragOver ? `${ACCENT2}08` : "transparent" }}
                >
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={DIM} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 10 }}><path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"/><polyline points="7 9 12 4 17 9"/><line x1="12" y1="4" x2="12" y2="16"/></svg>
                  <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: MID, marginBottom: 4 }}>
                    Drag &amp; drop your .xlsx file here, or{" "}
                    <span style={{ color: ACCENT, textDecoration: "underline" }}>browse</span>
                  </div>
                  <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 11, color: DIM }}>
                    Excel files only (.xlsx) · Use the Canopy template above
                  </div>
                </div>
                <input ref={fileInputRef} type="file" accept=".xlsx" style={{ display: "none" }}
                  onChange={e => { const f = e.target.files[0]; if (f) handleFile(f); e.target.value = ''; }}/>
              </>
            )}
          </>
        )}

        {/* Preview */}
        {step === 'preview' && parsedData && (
          <>
            <div className="r-wrap" style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, marginBottom: 18 }}>
              <div style={{ minWidth: 0, overflowWrap: "anywhere" }}>
                <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 13, fontWeight: 600, color: DARK, marginBottom: 3 }}>{fileName}</div>
                <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, color: DIM }}>
                  {validJobCount} job{validJobCount !== 1 ? 's' : ''} · {validRevCount} revenue row{validRevCount !== 1 ? 's' : ''} · {validExpCount} expense row{validExpCount !== 1 ? 's' : ''}
                </div>
              </div>
              <button className="btn" onClick={() => { setStep('idle'); setParsedData(null); setFileName(''); }}>← Different file</button>
            </div>

            {plan && (
              <div style={{ background: BG2, border: `1px solid ${BORDER}`, borderRadius: 6, padding: "12px 16px", marginBottom: 16 }}>
                <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 13, fontWeight: 600, color: DARK }}>{summaryLine(plan.summary)}</div>
                {nothingToDo && (
                  <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: MID, marginTop: 6 }}>
                    This file is already imported, so nothing would change.
                  </div>
                )}
                <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 11, color: DIM, marginTop: 6, lineHeight: 1.6 }}>
                  Rows already imported are recognized and not added again. Amounts that changed are updated. Rows that are not in this file are never removed.
                </div>
              </div>
            )}

            {plan && plan.summary.undated > 0 && (
              <div role="status" style={{ background: `${ACCENT}0D`, border: `1px solid ${ACCENT}33`, borderRadius: 6, padding: "12px 16px", marginBottom: 16, fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: DARK, lineHeight: 1.6 }}>
                {undatedNotice(plan.summary)}
                {plan.summary.unreadableDates > 0 && ` ${plan.summary.unreadableDates} of them had a date we could not read.`}
              </div>
            )}

            {hasErrors && (
              <div style={{ background: `${RED}0D`, border: `1px solid ${RED}33`, borderRadius: 6, padding: "12px 16px", marginBottom: 16 }}>
                <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12, fontWeight: 600, color: RED, marginBottom: 5 }}>
                  {parsedData.errors.length} issue{parsedData.errors.length !== 1 ? 's' : ''} found
                </div>
                {parsedData.errors.map((e, i) => (
                  <div key={i} style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 11, color: RED, lineHeight: 1.8 }}>· {e}</div>
                ))}
                <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 11, color: MID, marginTop: 8 }}>
                  Rows with errors will be skipped. Valid rows will still be imported.
                </div>
              </div>
            )}

            {parsedData.jobs.length > 0 && (
              <div style={{ marginBottom: 18 }}>
                <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 10, fontWeight: 700, color: DIM, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>Jobs to import</div>
                <div style={{ border: `1px solid ${BORDER}`, borderRadius: 6, overflow: "hidden" }}>
                  <div className="r-scroll">
                  <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "'DM Sans',sans-serif", fontSize: 12, minWidth: 440 }}>
                    <thead>
                      <tr style={{ background: BG2 }}>
                        <th style={{ padding: "7px 12px", textAlign: "left", color: DIM, fontWeight: 500 }}>Job Name</th>
                        <th style={{ padding: "7px 12px", textAlign: "left", color: DIM, fontWeight: 500 }}>Client</th>
                        <th style={{ padding: "7px 12px", textAlign: "left", color: DIM, fontWeight: 500 }}>Type</th>
                        <th style={{ padding: "7px 12px", textAlign: "left", color: DIM, fontWeight: 500 }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedData.jobs.map((j, i) => (
                        <tr key={i} style={{ borderTop: `1px solid ${BORDER}` }}>
                          <td style={{ padding: "7px 12px", color: DARK }}>{j.name}</td>
                          <td style={{ padding: "7px 12px", color: MID }}>{j.clientName || '—'}</td>
                          <td style={{ padding: "7px 12px", color: MID }}>{j.jobType || 'General'}</td>
                          <td style={{ padding: "7px 12px", color: MID }}>{j.status || 'In Progress'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>
              </div>
            )}

            {importError && (
              <div style={{ background: `${RED}0D`, border: `1px solid ${RED}33`, borderRadius: 6, padding: "10px 14px", marginBottom: 14, fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: RED }}>
                Import failed: {importError}
              </div>
            )}

            <div className="r-btnrow" style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button className="btn" onClick={() => { setStep('idle'); setParsedData(null); setFileName(''); }}>Cancel</button>
              <button className="btn act" onClick={handleImport} disabled={validJobCount === 0 || !plan || nothingToDo}>
                Import
              </button>
            </div>
          </>
        )}

        {/* Importing */}
        {step === 'importing' && (
          <div style={{ textAlign: "center", padding: "28px 0" }}>
            <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: MID }}>Importing data…</div>
          </div>
        )}

        {/* Success */}
        {step === 'success' && (
          <div className="r-wrap" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <div style={{ width: 18, height: 18, borderRadius: "50%", background: ACCENT2, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "#fff", flexShrink: 0 }}>✓</div>
                <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 13, fontWeight: 600, color: DARK }}>Import complete</span>
              </div>
              <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: MID, paddingLeft: 26 }}>
                {plan ? summaryLine(plan.summary) : 'Done'}. Visible in Dashboard and Job Detail.
              </div>
            </div>
            <button className="btn" onClick={() => { setStep('idle'); setParsedData(null); setFileName(''); }}>Upload another</button>
          </div>
        )}
      </div>

      {/* Upload history */}
      <div>
        <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 10, fontWeight: 700, color: DIM, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>Upload History</div>
        {loadingBatches && <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: DIM }}>Loading…</div>}
        {!loadingBatches && batches.length === 0 && (
          <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: DIM }}>No uploads yet.</div>
        )}
        {deleteMessage && (
          <div role="alert" style={{ background: deleteFailed ? `${RED}0D` : BG2, border: `1px solid ${deleteFailed ? `${RED}33` : BORDER}`, borderRadius: 6, padding: "10px 14px", marginBottom: 12, fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: deleteFailed ? RED : DARK, lineHeight: 1.6 }}>
            {deleteMessage}
          </div>
        )}
        {batches.map(b => (
          <div key={b.id} style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 8, padding: "13px 18px", marginBottom: 8, display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 13, fontWeight: 500, color: DARK, marginBottom: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{b.filename}</div>
              <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, color: DIM }}>
                {b.job_count} job{b.job_count !== 1 ? 's' : ''} · {b.revenue_count} revenue · {b.expense_count} expense{b.expense_count !== 1 ? 's' : ''}
                <span style={{ marginLeft: 10 }}>· {new Date(b.uploaded_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
              </div>
            </div>
            <button className="btn" onClick={() => handleDeleteBatch(b.id)} disabled={deletingBatch === b.id}
              style={{ color: RED, borderColor: `${RED}44`, fontSize: 11, padding: "5px 12px", flexShrink: 0 }}>
              {deletingBatch === b.id ? 'Deleting…' : 'Delete batch'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export { UploadData };
